import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchProfileFees, fetchPriceHistory, fetchProvenance, fetchTopHashtags } from '../thunks/profileThunks';

export interface ProfileFees {
  dmFee: string;
  dmSpotlightFee: string;
  viewFee: string;
  liveSessionFee: string;
  consultationFee: string;
  subscriptionFee: string;
  subscriptionPeriodSeconds: number;
}

export interface PricePoint {
  price: number;
  date: string;
}

export interface HashtagEntry {
  tag: string;
  count: number;
}

export interface ProvenanceEntry {
  txType: 'create' | 'buy';
  fromWallet: string;
  toWallet: string | null;
  priceKlv: number | null;
  txHash: string;
  date: string;
}

interface ProfileState {
  feesByUserId: Record<string, ProfileFees>;
  feesLoadingUserId: string | null;
  feesError: string | null;
  // Keyed by username
  priceHistoryByUsername: Record<string, PricePoint[]>;
  priceHistoryLoadingUsername: string | null;
  provenanceByUsername: Record<string, ProvenanceEntry[]>;
  provenanceLoadingUsername: string | null;
  topHashtagsByUsername: Record<string, HashtagEntry[]>;
  topHashtagsLoadingUsername: string | null;
}

const initialState: ProfileState = {
  feesByUserId: {},
  feesLoadingUserId: null,
  feesError: null,
  priceHistoryByUsername: {},
  priceHistoryLoadingUsername: null,
  provenanceByUsername: {},
  provenanceLoadingUsername: null,
  topHashtagsByUsername: {},
  topHashtagsLoadingUsername: null,
};

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    clearProfileFees: (state, action: PayloadAction<string>) => {
      delete state.feesByUserId[action.payload];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProfileFees.pending, (state, action) => {
        state.feesLoadingUserId = action.meta.arg;
        state.feesError = null;
      })
      .addCase(fetchProfileFees.fulfilled, (state, action) => {
        state.feesByUserId[action.meta.arg] = action.payload;
        state.feesLoadingUserId = null;
      })
      .addCase(fetchProfileFees.rejected, (state, action) => {
        state.feesLoadingUserId = null;
        state.feesError = action.payload as string;
      })
      .addCase(fetchPriceHistory.pending, (state, action) => {
        state.priceHistoryLoadingUsername = action.meta.arg;
      })
      .addCase(fetchPriceHistory.fulfilled, (state, action) => {
        state.priceHistoryByUsername[action.meta.arg] = action.payload;
        state.priceHistoryLoadingUsername = null;
      })
      .addCase(fetchPriceHistory.rejected, (state) => {
        state.priceHistoryLoadingUsername = null;
      })
      .addCase(fetchProvenance.pending, (state, action) => {
        state.provenanceLoadingUsername = action.meta.arg;
      })
      .addCase(fetchProvenance.fulfilled, (state, action) => {
        state.provenanceByUsername[action.meta.arg] = action.payload;
        state.provenanceLoadingUsername = null;
      })
      .addCase(fetchProvenance.rejected, (state) => {
        state.provenanceLoadingUsername = null;
      })
      .addCase(fetchTopHashtags.pending, (state, action) => {
        state.topHashtagsLoadingUsername = action.meta.arg;
      })
      .addCase(fetchTopHashtags.fulfilled, (state, action) => {
        state.topHashtagsByUsername[action.meta.arg] = action.payload;
        state.topHashtagsLoadingUsername = null;
      })
      .addCase(fetchTopHashtags.rejected, (state) => {
        state.topHashtagsLoadingUsername = null;
      });
  },
});

export const { clearProfileFees } = profileSlice.actions;

// Selectors
export const selectProfileFees = (userId: string) => (state: { profile: ProfileState }) =>
  state.profile.feesByUserId[userId] ?? null;

export const selectFeesLoading = (userId: string) => (state: { profile: ProfileState }) =>
  state.profile.feesLoadingUserId === userId;

export const selectPriceHistory = (username: string) => (state: { profile: ProfileState }) =>
  state.profile.priceHistoryByUsername[username] ?? null;

export const selectProvenance = (username: string) => (state: { profile: ProfileState }) =>
  state.profile.provenanceByUsername[username] ?? null;

export const selectTopHashtags = (username: string) => (state: { profile: ProfileState }) =>
  state.profile.topHashtagsByUsername[username] ?? null;

export default profileSlice.reducer;
